import { MASTER_KALA_PERSONA_ID } from '../coaching/constants';

/**
 * AI Assistant personas (Phase 1).
 *
 * A persona is a selectable "mode" for the member assistant: an emoji icon +
 * a system-prompt instruction block appended LAST in the prompt's stable
 * prefix. Off-platform role-play personas (Nurse, Mechanic, Tutor, Translator)
 * deliberately have `allowAccountTools: false` so they cannot read the
 * member's account data; the General assistant and the Business Coach (which
 * grounds coaching in the member's real standing) can. Tenants override/extend
 * the defaults via `features.assistant.personas[]` (merged by id) through the
 * admin persona editor.
 *
 * Personas can also declare `intake` questions — a small member profile the
 * persona asks for up front (the UI shows a card on the first chat; the
 * persona also asks naturally in-conversation) — and `suggestedQuestions`
 * starter chips, optionally templated on the intake answers.
 *
 * `icon` is an emoji; a persona may ALSO carry an `avatarKey` (an uploaded
 * image), which the member UI prefers and falls back from to the emoji.
 */

/**
 * One persona intake question. Answers are stored as `assistant_memory` rows
 * under `key` — the SAME store the `save_memory` tool writes — so an answer
 * given in the intake card and a fact shared mid-conversation converge, both
 * flow into the prompt seed, and "forget me" clears them together.
 */
export type AssistantIntakeQuestion = {
  /** snake_case memory key the answer is stored under (unique per member). */
  key: string;
  /** The question shown to the member (and asked by the persona in-chat). */
  question: string;
  /** Optional input placeholder/example for the intake card. */
  placeholder?: string;
};

export type AssistantPersona = {
  /** Stable id — override merging and conversation rows key on it. */
  id: string;
  name: string;
  /** Emoji shown when there's no avatar image (and as the image's fallback). */
  icon: string;
  /**
   * Uploaded avatar image (R2 object key, via the storefront asset upload).
   * Optional — the emoji stands in when it's absent or fails to load. Keys
   * must be reachable by `GET /storefront/asset`, so any new source of them
   * belongs in `collectAssistantAvatarKeys` below.
   */
  avatarKey?: string;
  /** One-line card subtitle shown in the persona picker. */
  tagline: string;
  /**
   * Persona instruction block, appended last in the system prompt's stable
   * prefix. Global guardrails (no earnings predictions, read-only, etc.) are
   * NOT repeated here — the assembler owns those.
   */
  instructions: string;
  /** May call the member-account tools (wallet, team, orders, rank…). */
  allowAccountTools: boolean;
  /** May use server-side web search (still gated by the tenant toggle). */
  allowWebSearch: boolean;
  /** Best-effort browser speech-synthesis voice hint (e.g. 'en-US'). */
  voiceHint?: string;
  /**
   * Member-profile questions this persona asks up front (Nurse → conditions,
   * Mechanic → vehicle…). Empty/omitted = persona has no intake.
   */
  intake?: AssistantIntakeQuestion[];
  /**
   * Starter-question chips shown on an empty thread. `{key}` placeholders
   * substitute the member's intake answer for that key; a chip whose
   * placeholder is unanswered is hidden (see `fillSuggestedQuestion`).
   */
  suggestedQuestions?: string[];
  /**
   * Tenant switched this persona off. It disappears from the picker and can't
   * start new threads; EXISTING threads on it keep working (a member mid-chat
   * shouldn't lose their conversation because an admin tidied the list).
   * Undefined means enabled — absence is the default for every built-in.
   */
  enabled?: boolean;
  /**
   * Tenant feature this persona needs. With the flag off the persona does not
   * exist for members: it is hidden, cannot start threads, and its existing
   * threads stop answering. Server-set on built-ins only; never taken from a
   * tenant override.
   */
  requiresFeature?: 'coaching_enabled';
  /**
   * A coaching persona (Master Kala): it gets the coaching tools and prompt
   * instead of the account tools, and only the coach app lists it. Server-set
   * on built-ins only; never taken from a tenant override.
   */
  coachingTools?: boolean;
};

/** Tenant override: same shape, everything optional except the id. */
export type AssistantPersonaOverride = Partial<AssistantPersona> & { id: string };

/** The tenant flags a persona can depend on (a structural subset of `TenantFeatures`). */
export type PersonaFeatureFlags = { coaching_enabled?: boolean };

/**
 * A persona the member may start a new chat with. A persona that depends on a
 * tenant feature reads as disabled unless `features` shows that feature on.
 */
export function personaEnabled(p: AssistantPersona, features?: PersonaFeatureFlags): boolean {
  if (p.enabled === false) return false;
  return p.requiresFeature ? features?.[p.requiresFeature] === true : true;
}

export const GENERAL_PERSONA_ID = 'general';

export const DEFAULT_PERSONAS: AssistantPersona[] = [
  {
    id: GENERAL_PERSONA_ID,
    name: 'Assistant',
    icon: '✨',
    tagline: 'Your all-around helper — account, platform, and general questions',
    instructions:
      'You are the member’s general-purpose personal assistant. Answer questions about ' +
      'their account and earnings using your tools, guide them around the platform, and ' +
      'help with everyday questions, writing, and translations like a capable human ' +
      'assistant would.',
    allowAccountTools: true,
    allowWebSearch: true,
    suggestedQuestions: [
      'How much have I earned this month?',
      'What do I still need for my next rank?',
      'How do I cash out my earnings?',
      'How is my team doing?',
    ],
  },
  {
    id: 'nurse',
    name: 'Nurse',
    icon: '🩺',
    tagline: 'Everyday health and wellness guidance',
    instructions:
      'You are a friendly, knowledgeable nurse. Give practical everyday health and ' +
      'wellness guidance in plain language. You are not a doctor and never diagnose or ' +
      'prescribe — for anything serious, urgent, or medication-related, tell the member ' +
      'to see a licensed medical professional.',
    allowAccountTools: false,
    allowWebSearch: true,
    intake: [
      {
        key: 'health_conditions',
        question: 'Any ongoing health conditions I should keep in mind?',
        placeholder: 'e.g. high blood pressure, diabetes — or "none"',
      },
      {
        key: 'health_meds_allergies',
        question: 'Any regular medications or allergies?',
        placeholder: 'e.g. losartan daily; allergic to penicillin',
      },
    ],
    suggestedQuestions: [
      'What should I watch out for day to day with {health_conditions}?',
      'What foods are good for someone like me?',
      'How can I sleep better?',
    ],
  },
  {
    id: 'mechanic',
    name: 'Mechanic',
    icon: '🔧',
    tagline: 'Vehicle care and troubleshooting help',
    instructions:
      'You are an experienced motor vehicle mechanic. Help the member troubleshoot car ' +
      'and motorcycle problems step by step, explain likely causes and rough repair ' +
      'options, and flag anything that is unsafe to drive with or needs a licensed shop.',
    allowAccountTools: false,
    allowWebSearch: true,
    intake: [
      {
        key: 'vehicle',
        question: 'What do you drive? (make, model, year)',
        placeholder: 'e.g. Toyota Vios 2018, Honda Click 125',
      },
      {
        key: 'vehicle_concerns',
        question: 'Anything currently bothering you about it?',
        placeholder: 'e.g. squealing when braking — or "nothing right now"',
      },
    ],
    suggestedQuestions: [
      'What regular maintenance does my {vehicle} need?',
      'Help me figure out this problem: {vehicle_concerns}',
      'What should I check before a long drive?',
    ],
  },
  {
    id: 'business-coach',
    name: 'Business Coach',
    icon: '📈',
    tagline: 'Selling skills and network-building coaching',
    instructions:
      'You are a supportive direct-selling business coach. Coach the member on selling ' +
      'skills, customer service, follow-ups, and growing their network ethically. Use ' +
      'your account tools to ground advice in their REAL standing (rank progress, team, ' +
      'actual earnings) instead of generic tips. Focus on effort and skills — never ' +
      'promise, project, or imply any income outcome.',
    allowAccountTools: true,
    allowWebSearch: true,
    intake: [
      {
        key: 'business_goal',
        question: 'What is your main goal in the business right now?',
        placeholder: 'e.g. reach Silver rank, 5 new customers this month',
      },
      {
        key: 'business_experience',
        question: 'How long have you been in direct selling, and what do you sell?',
        placeholder: 'e.g. 6 months, wellness products',
      },
    ],
    suggestedQuestions: [
      'What should I do this week to move toward: {business_goal}?',
      'How do I follow up with a prospect without being pushy?',
      'Give me a simple daily routine for growing my business.',
    ],
  },
  {
    id: 'tutor',
    name: 'Tutor',
    icon: '📚',
    tagline: 'Patient explanations for any subject',
    instructions:
      'You are a patient tutor. Explain concepts simply, check understanding with small ' +
      'questions, and adapt to the member’s level. Prefer worked examples over ' +
      'lectures.',
    allowAccountTools: false,
    allowWebSearch: true,
    intake: [
      {
        key: 'study_focus',
        question: 'Who is studying, and what subject or level?',
        placeholder: 'e.g. my daughter, Grade 6 math; me, basic English',
      },
    ],
    suggestedQuestions: [
      'Make a simple study plan for {study_focus}.',
      'Explain a topic I find confusing.',
      'Quiz me on what I have been studying.',
    ],
  },
  {
    id: 'translator',
    name: 'Translator',
    icon: '🌐',
    tagline: 'Translate between languages, naturally',
    instructions:
      'You are a translator. Translate whatever the member sends into the language they ' +
      'ask for (default: between English and Filipino), preserving tone and meaning. ' +
      'When a phrase is idiomatic, give the natural equivalent plus a literal gloss.',
    allowAccountTools: false,
    allowWebSearch: false,
    suggestedQuestions: [
      'Translate a message to English for me.',
      'How do I say this politely in Filipino?',
    ],
  },
  {
    id: MASTER_KALA_PERSONA_ID,
    name: 'Master Kala',
    icon: '🪷',
    tagline: 'Your success coach',
    instructions:
      'Coach the member toward their primary goal through clear decisions and daily action. ' +
      'Be calm, warm and direct, and adapt to their chosen coaching style.',
    allowAccountTools: false,
    allowWebSearch: false,
    requiresFeature: 'coaching_enabled',
    coachingTools: true,
    suggestedQuestions: [
      'Plan my day',
      'Help me get unstuck',
      'Review my biggest goal',
      'Help me improve my mindset',
      'What should I focus on next?',
    ],
  },
];

/**
 * Merge tenant persona overrides over the defaults by id. Overrides with a
 * known id patch that persona; overrides with a new id append a new persona
 * (missing fields fall back to safe values — account tools OFF).
 */
export function resolvePersonas(overrides?: AssistantPersonaOverride[] | null): AssistantPersona[] {
  if (!overrides?.length) return DEFAULT_PERSONAS;
  const out = DEFAULT_PERSONAS.map((p) => ({ ...p }));
  for (const raw of overrides) {
    if (!raw?.id) continue;
    // Feature gating and the coaching tool family are server-set on built-ins:
    // a tenant override can never grant or remove them.
    const { requiresFeature: _requiresFeature, coachingTools: _coachingTools, ...o } = raw;
    const existing = out.find((p) => p.id === o.id);
    if (existing) {
      Object.assign(existing, stripUndefined(o));
    } else {
      out.push({
        id: o.id,
        name: o.name ?? o.id,
        icon: o.icon ?? '🤖',
        tagline: o.tagline ?? '',
        instructions: o.instructions ?? '',
        allowAccountTools: o.allowAccountTools ?? false,
        allowWebSearch: o.allowWebSearch ?? false,
        ...(o.avatarKey ? { avatarKey: o.avatarKey } : {}),
        ...(o.voiceHint ? { voiceHint: o.voiceHint } : {}),
        ...(o.intake ? { intake: o.intake } : {}),
        ...(o.suggestedQuestions ? { suggestedQuestions: o.suggestedQuestions } : {}),
        ...(o.enabled === false ? { enabled: false } : {}),
      });
    }
  }
  return out;
}

function stripUndefined<T extends object>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as Partial<T>;
}

// ── Intake / memory-key helpers ─────────────────────────────────────────────

export const ASSISTANT_MEMORY_KEY_MAX = 64;
export const ASSISTANT_MEMORY_VALUE_MAX = 500;

/**
 * Canonical memory-key cleaning — shared by the `save_memory` tool and the
 * intake route so both paths land on the SAME `assistant_memory` row for a
 * given fact. Returns '' when nothing survives.
 */
export function normalizeAssistantMemoryKey(key: string): string {
  return key
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, ASSISTANT_MEMORY_KEY_MAX);
}

/**
 * "Don't ask again" marker rows: `intake_skipped_<persona>` in
 * `assistant_memory`. Filtered out of the prompt seed and the personas DTO —
 * they are UI state, not knowledge about the member.
 */
export const ASSISTANT_INTAKE_SKIP_PREFIX = 'intake_skipped_';

export function assistantIntakeSkipKey(personaId: string): string {
  return normalizeAssistantMemoryKey(`${ASSISTANT_INTAKE_SKIP_PREFIX}${personaId}`);
}

/**
 * A persona's intake list with tenant-override garbage dropped: entries must
 * be objects with a non-empty normalizable key and a question; keys are
 * normalized so every consumer (route, prompt, UI) agrees on the stored form.
 */
export function personaIntake(persona: AssistantPersona): AssistantIntakeQuestion[] {
  if (!Array.isArray(persona.intake)) return [];
  const out: AssistantIntakeQuestion[] = [];
  const seen = new Set<string>();
  for (const q of persona.intake) {
    if (!q || typeof q !== 'object') continue;
    const key = normalizeAssistantMemoryKey(String(q.key ?? ''));
    const question = typeof q.question === 'string' ? q.question.trim() : '';
    if (!key || !question || seen.has(key) || key.startsWith(ASSISTANT_INTAKE_SKIP_PREFIX))
      continue;
    seen.add(key);
    out.push({
      key,
      question,
      ...(typeof q.placeholder === 'string' && q.placeholder ? { placeholder: q.placeholder } : {}),
    });
  }
  return out;
}

/** Every intake key declared by ANY persona (for seed partitioning). */
export function allPersonaIntakeKeys(personas: AssistantPersona[]): Set<string> {
  const keys = new Set<string>();
  for (const p of personas) for (const q of personaIntake(p)) keys.add(q.key);
  return keys;
}

/**
 * Avatar object keys referenced by persona overrides. `GET /storefront/asset`
 * only serves allowlisted keys, and personas live in `features.assistant`, not
 * in the storefront config the storefront collector walks — so without this
 * every uploaded avatar 404s.
 */
export function collectAssistantAvatarKeys(
  overrides?: AssistantPersonaOverride[] | null,
): Set<string> {
  const keys = new Set<string>();
  for (const o of overrides ?? []) {
    const key = o?.avatarKey?.trim();
    // Baked public paths ('/img/...') are served by the SPA, not the asset route.
    if (key && !key.startsWith('/')) keys.add(key);
  }
  return keys;
}

/**
 * Fill a suggested-question template's `{key}` placeholders from the member's
 * intake answers. Returns null when any referenced key is unanswered (the chip
 * is hidden until the member has shared that fact). Long answers are clipped
 * so a chip stays chip-sized.
 */
export function fillSuggestedQuestion(
  template: string,
  answers: Record<string, string>,
): string | null {
  let missing = false;
  const filled = template.replace(/\{([a-z0-9_]+)\}/g, (_m, key: string) => {
    const answer = answers[key]?.trim();
    if (!answer) {
      missing = true;
      return '';
    }
    return answer.length > 60 ? `${answer.slice(0, 57)}…` : answer;
  });
  return missing ? null : filled;
}
